import { useState } from 'react';
import { Button } from '../ui/button';
import { DateInput } from '../ui/date-input';
import { useOwnerStore } from '@/lib/owner-store';
import { useEffect } from 'react';

interface ScrumUpdateFormProps {
  initialData?: {
    owner_id: string;
    yesterday: string;
    today: string;
    blockers?: string;
    date: string;
  };
  onSubmit: (data: {
    owner_id: string;
    yesterday: string;
    today: string;
    blockers?: string;
    date: string;
  }) => Promise<void>;
  onCancel: () => void;
}

export function ScrumUpdateForm({ initialData, onSubmit, onCancel }: ScrumUpdateFormProps) {
  const { owners, fetchOwners } = useOwnerStore();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialData || {
    owner_id: '',
    yesterday: '',
    today: '',
    blockers: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-md">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Owner</label>
        <select
          name="owner_id"
          value={formData.owner_id}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          required
        >
          <option value="">Select owner...</option>
          {owners.map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.name} {owner.organization ? `(${owner.organization})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Date</label>
        <DateInput
          name="date"
          value={formData.date}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">What did you do yesterday?</label>
        <textarea
          name="yesterday"
          value={formData.yesterday}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={3}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">What will you do today?</label>
        <textarea
          name="today"
          value={formData.today}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={3}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Any blockers?</label>
        <textarea
          name="blockers"
          value={formData.blockers}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={2}
          placeholder="None"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Update'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}